(function () {
  const ANIMATION_MS = 450;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileLayout() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  function waitForPanelTransition(panel, propertyName = 'height') {
    return new Promise((resolve) => {
      if (!panel || prefersReducedMotion()) {
        resolve();
        return;
      }

      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        panel.removeEventListener('transitionend', onEnd);
        resolve();
      };

      const onEnd = (event) => {
        if (event.target !== panel) return;
        if (event.propertyName !== propertyName) return;
        finish();
      };

      const timeoutId = window.setTimeout(finish, ANIMATION_MS + 100);
      panel.addEventListener('transitionend', onEnd);
    });
  }

  function setExpandedState(section, isExpanded) {
    const toggles = section.querySelectorAll('[data-biomarkers-diagram-toggle]');

    for (const toggle of toggles) {
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }
  }

  function updateOpenFrameHeight(section) {
    const frame = section.querySelector('.biomarkers-diagram__frame');
    const panel = section.querySelector('[data-biomarkers-diagram-panel]');

    if (!(frame instanceof HTMLElement) || !(panel instanceof HTMLElement) || panel.hidden) return;
    if (isMobileLayout()) {
      frame.style.height = '';
      return;
    }

    // The core is sized to 918 design units, so it yields the current unit scale.
    const core = section.querySelector('.biomarkers-diagram__core');
    const unit = core instanceof HTMLElement && core.offsetHeight > 0 ? core.offsetHeight / 918 : 1;
    const requiredHeight = panel.offsetTop + panel.scrollHeight + 90 * unit;

    frame.style.height = `${requiredHeight}px`;
  }

  async function openDiagram(section) {
    if (section.classList.contains('is-open') || section.classList.contains('is-animating')) return;

    const panel = section.querySelector('[data-biomarkers-diagram-panel]');
    const openToggle = section.querySelector('.biomarkers-diagram__toggle--open');

    if (!panel) return;

    section.classList.add('is-animating', 'is-open');
    setExpandedState(section, true);
    panel.hidden = false;
    updateOpenFrameHeight(section);

    if (prefersReducedMotion() || isMobileLayout()) {
      panel.style.height = '';
      section.classList.remove('is-animating');
      openToggle?.focus({ preventScroll: true });
      return;
    }

    panel.style.height = '0px';
    await nextFrame();
    updateOpenFrameHeight(section);
    panel.style.height = `${panel.scrollHeight}px`;

    await waitForPanelTransition(panel);

    panel.style.height = '';
    section.classList.remove('is-animating');
    openToggle?.focus({ preventScroll: true });
  }

  async function closeDiagram(section) {
    if (!section.classList.contains('is-open') || section.classList.contains('is-animating')) return;

    const panel = section.querySelector('[data-biomarkers-diagram-panel]');
    const closedToggle = section.querySelector('.biomarkers-diagram__toggle--closed');

    if (!panel) return;

    section.classList.add('is-animating');
    setExpandedState(section, false);

    if (prefersReducedMotion() || isMobileLayout()) {
      section.classList.remove('is-open', 'is-animating');
      panel.hidden = true;
      panel.style.height = '';
      const frame = section.querySelector('.biomarkers-diagram__frame');
      if (frame instanceof HTMLElement) frame.style.height = '';
      closedToggle?.focus({ preventScroll: true });
      return;
    }

    // Slide the panel down out of the card rather than collapsing its height,
    // so it exits downward and is clipped by the frame instead of the panel clip.
    const inner = panel.querySelector('.biomarkers-diagram__panel');
    const frame = section.querySelector('.biomarkers-diagram__frame');

    section.classList.add('is-closing');
    section.classList.remove('is-open');
    if (frame instanceof HTMLElement) frame.style.height = '';

    await waitForPanelTransition(inner, 'transform');

    section.classList.remove('is-closing');
    panel.hidden = true;
    panel.style.height = '';
    section.classList.remove('is-animating');
    closedToggle?.focus({ preventScroll: true });
  }

  function initDiagram(section) {
    if (!section || section.dataset.biomarkersDiagramInitialized === 'true') return;

    section.biomarkersDiagramResizeController?.abort();
    section.dataset.biomarkersDiagramInitialized = 'true';

    const resizeController = new AbortController();
    const toggles = section.querySelectorAll('[data-biomarkers-diagram-toggle]');
    section.biomarkersDiagramResizeController = resizeController;

    for (const toggle of toggles) {
      toggle.addEventListener('click', () => {
        if (section.classList.contains('is-open')) {
          closeDiagram(section);
          return;
        }

        openDiagram(section);
      }, { signal: resizeController.signal });
    }

    window.addEventListener('resize', () => {
      if (!section.classList.contains('is-open')) return;

      const frame = section.querySelector('.biomarkers-diagram__frame');
      if (frame instanceof HTMLElement) frame.style.height = '';
      updateOpenFrameHeight(section);
    }, { signal: resizeController.signal });
  }

  function initAll() {
    const sections = document.querySelectorAll('[data-biomarkers-diagram]');

    for (const section of sections) {
      initDiagram(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-biomarkers-diagram]') || event.target;
    if (!section?.matches?.('[data-biomarkers-diagram]')) return;

    delete section.dataset.biomarkersDiagramInitialized;
    initDiagram(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target?.querySelector?.('[data-biomarkers-diagram]') || event.target;
    if (!section?.matches?.('[data-biomarkers-diagram]')) return;

    section.biomarkersDiagramResizeController?.abort();
    delete section.biomarkersDiagramResizeController;
    delete section.dataset.biomarkersDiagramInitialized;
  });
})();
